import axios from 'axios'
import {
  API_URL,
  GOOGLE_SOLAR_API_KEY,
  GOOGLE_SOLAR_DEFAULT_QUALITY,
  MAP_TILE_SCALE,
  MAP_TILE_SIZE,
  MAP_TILE_ZOOM,
  estimateMapStaticCoverageMeters,
} from './CONSTANT'
import {
  buildGoogleSolarSummary,
  buildLidarFromGoogleSolarDataLayers,
  buildSyntheticLidarFromBuildingInsights,
  fetchGoogleSolarBuildingInsights,
  fetchGoogleSolarDataLayers,
  normalizeRoofSegmentStats,
} from "./googleSolarUtils";
import { fetchDronifyPlyLidar } from "./PlyLidar";

// Default API configuration with headers for authorization
const apiConfig = {
  headers: {
    Accept: 'application/json',  // Set the Accept header to expect JSON responses
  },
  // Method to set Authorization header for API requests
  setAuthorization: (token) => {
    apiConfig.headers.Authorization = `Bearer ${token}`;  // Set the Bearer token in the Authorization header
  },
}

// Function to fetch design details by design ID
export const getDesign = async (token, designId) => {
  apiConfig.setAuthorization(token);  // Set authorization token

  try {
    // Make GET request to fetch the design by ID
    const response = await axios.get(`${API_URL}/designer/${designId}`, apiConfig);
    return response;  // Return the response object if successful
  } catch (error) {
    throw error;  // Throw an error if request fails
  }
}

// Function to download the model associated with the design ID
export const downloadModel = async (token, designId) => {
  apiConfig.setAuthorization(token);  // Set authorization token

  try {
    // Make GET request to download the model by design ID
    const response = await axios.get(`${API_URL}/projects/download/${designId}`, apiConfig);
    return response;  // Return the response object if successful
  } catch (error) {
    throw error;  // Throw an error if request fails
  }
}

// Function to update project data by project ID
export const updateProjectData = async (token, projectID, data) => {
  apiConfig.setAuthorization(token);  // Set authorization token

  const options = {
    method: 'POST',  // Set method to POST for updating data
    url: `${API_URL}/designer/${projectID}`,  // Target URL with project ID
    headers: apiConfig.headers,  // Use the configured headers (including authorization)
    data,  // Attach the data payload for the update
  }

  try {
    // Make POST request to update project data
    const response = await axios.request(options);
    return response;  // Return the response object if successful
  } catch (error) {
    throw error;  // Throw an error if request fails
  }
}

const postProjectStatusTransition = async (token, projectId, action, data = undefined) => {
  apiConfig.setAuthorization(token);

  const options = {
    method: 'POST',
    url: `${API_URL}/projects/${projectId}/${action}`,
    headers: apiConfig.headers,
    ...(data !== undefined ? { data } : {}),
  };

  try {
    const response = await axios.request(options);
    return response;
  } catch (error) {
    throw error;
  }
};

export const markProjectIncomplete = async (token, projectId) =>
  postProjectStatusTransition(token, projectId, 'incomplete');

export const markProjectComplete = async (token, projectId, rating) =>
  postProjectStatusTransition(token, projectId, 'complete', { rating });

// Function to check if the user is authenticated (check user)
export const checkUser = async (token) => {
  apiConfig.setAuthorization(token);  // Set authorization token

  const options = {
    method: 'POST',  // Set method to POST for checking user data
    url: `${API_URL}/me`,  // Target URL to verify the user
    headers: apiConfig.headers,  // Use the configured headers (including authorization)
    data: {},  // Empty data object for the request body
  }

  try {
    // Make POST request to check user information
    const response = await axios.request(options);
    return response;  // Return the response object if successful
  } catch (error) {
    throw error;  // Throw an error if request fails
  }
}

// Page size requested from /panels — big enough that the whole catalog usually
// arrives in a single response when the API honours `per_page`
const PANEL_PAGE_SIZE = 500;

// The /panels endpoint returns a Laravel paginator, which may sit at the payload
// root or one level down under `data`. Walk down until the items array is found.
const findPaginator = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  if (Array.isArray(payload.data)) return payload;  // { data: [...], total, per_page, ... }
  if (payload.data && typeof payload.data === 'object') return findPaginator(payload.data);
  return null;
}

// Function to fetch the list of panels available (all pages, not just the first)
export const getPanelList = async (token) => {
  apiConfig.setAuthorization(token);  // Set authorization token

  try {
    // Make GET request to fetch the panel list
    const response = await axios.get(`${API_URL}/panels`, {
      ...apiConfig,
      params: { per_page: PANEL_PAGE_SIZE },
    });

    const paginator = findPaginator(response.data);
    if (!paginator) return response;  // Not paginated — nothing else to fetch

    const items = [...paginator.data];
    const total = Number(paginator.total) || items.length;
    const perPage = Number(paginator.per_page) || items.length || PANEL_PAGE_SIZE;
    const lastPage = Number(paginator.last_page) || Math.ceil(total / perPage) || 1;

    // The API can cap `per_page` server-side, so pull the remaining pages too
    if (items.length < total && lastPage > 1) {
      const pages = [];
      for (let page = 2; page <= lastPage; page += 1) pages.push(page);

      const responses = await Promise.all(
        pages.map((page) =>
          axios.get(`${API_URL}/panels`, { ...apiConfig, params: { page, per_page: perPage } })
        )
      );

      responses.forEach((res) => {
        const pageData = findPaginator(res.data);
        if (pageData) items.push(...pageData.data);
      });
    }

    // Drop any duplicates the pagination window may have produced
    const seen = new Set();
    paginator.data = items.filter((panel) => {
      const key = panel?.id ?? panel;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return response;  // Same response shape, with the full catalog in place
  } catch (error) {
    throw error;  // Throw an error if request fails
  }
}

// Function to fetch panel images from a specific URL
export const getPanelImages = async (url) => {
  try {
    // Make GET request to fetch the panel image with response type as 'blob'
    const response = await axios.get(url, { responseType: 'blob' });
    return response;  // Return the response object if successful
  } catch (error) {
    throw error;  // Throw an error if request fails
  }
}

// Function to fetch project images by project ID
const getProjectImages = async (token, projectId) => {
  apiConfig.setAuthorization(token);  // Set authorization token

  const options = {
    method: 'POST',  // Set method to POST for fetching project images
    url: `${API_URL}/projects/images/${projectId}`,  // Target URL to fetch images by project ID
    headers: apiConfig.headers,  // Use the configured headers (including authorization)
    data: {},  // Empty data object for the request body
  }

  try {
    // Make POST request to fetch project images
    const response = await axios.request(options);
    return response;  // Return the response object if successful
  } catch (error) {
    throw error;  // Throw an error if request fails
  }
}

// Export the getProjectImages function for external usage
export { getProjectImages };

const LIDAR_URL = `https://lider.dronifyit.com`

const SOLAR_LOCATION_PRECISION = 6;
const SOLAR_PIXEL_SIZE_OPTIONS = [0.1, 0.25, 0.5, 1.0];
const SOLAR_FULL_LAYERS_MAX_RADIUS_METERS = 175;
const SOLAR_EARTH_METERS_PER_DEG_LAT = 110540;
const SOLAR_BUILDING_INSIGHTS_NEARBY_RADII_METERS = [12, 24, 36, 48];
const SOLAR_BUILDING_INSIGHTS_NEARBY_BEARINGS_DEGREES = [0, 45, 90, 135, 180, 225, 270, 315];

const solarInsightsCache = new Map();
const solarDataLayersCache = new Map();

const toFiniteNumber = (value, fallback = null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseCoordinate = (value) => {
  const parsed = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toSolarLocationKey = (latitude, longitude) => {
  const lat = parseCoordinate(latitude);
  const lng = parseCoordinate(longitude);
  if (lat == null || lng == null) return null;
  return `${lat.toFixed(SOLAR_LOCATION_PRECISION)},${lng.toFixed(SOLAR_LOCATION_PRECISION)}`;
};

const metersPerDegreeLngAtLat = (latitude) => {
  const lat = toFiniteNumber(latitude, 0);
  const latRad = (lat * Math.PI) / 180;
  return Math.max(1e-6, 111320 * Math.cos(latRad));
};

const offsetLatLngByMeters = (latitude, longitude, eastMeters = 0, northMeters = 0) => {
  const lat = toFiniteNumber(latitude, null);
  const lng = toFiniteNumber(longitude, null);
  if (lat == null || lng == null) return null;

  return {
    latitude: lat + northMeters / SOLAR_EARTH_METERS_PER_DEG_LAT,
    longitude: lng + eastMeters / metersPerDegreeLngAtLat(lat),
  };
};

const pickSupportedSolarPixelSize = (radiusMeters, preferred = SOLAR_PIXEL_SIZE_OPTIONS[0]) => {
  const radius = Math.max(0, toFiniteNumber(radiusMeters, 72));
  const preferredValue = toFiniteNumber(preferred, SOLAR_PIXEL_SIZE_OPTIONS[0]);

  if (radius <= 100) {
    return SOLAR_PIXEL_SIZE_OPTIONS.includes(preferredValue) ? preferredValue : 0.1;
  }

  const requiredMinimum = radius / 1000;
  for (const option of SOLAR_PIXEL_SIZE_OPTIONS) {
    if (option + 1e-9 >= requiredMinimum) return option;
  }

  return SOLAR_PIXEL_SIZE_OPTIONS[SOLAR_PIXEL_SIZE_OPTIONS.length - 1];
};

const toSolarDataLayersCacheKey = (
  latitude,
  longitude,
  {
    radiusMeters = 100,
    view = "FULL_LAYERS",
    requiredQuality = GOOGLE_SOLAR_DEFAULT_QUALITY,
    exactQualityRequired = false,
    pixelSizeMeters = 0.1,
  } = {}
) => {
  const locationKey = toSolarLocationKey(latitude, longitude);
  if (!locationKey) return null;

  const radius = toFiniteNumber(radiusMeters, 100);
  const pixelSize = toFiniteNumber(pixelSizeMeters, 0.1);
  const quality = String(requiredQuality || GOOGLE_SOLAR_DEFAULT_QUALITY);
  const layerView = String(view || "FULL_LAYERS");
  const exact = exactQualityRequired ? "1" : "0";

  return [
    locationKey,
    `r=${radius.toFixed(3)}`,
    `px=${pixelSize.toFixed(3)}`,
    `v=${layerView}`,
    `q=${quality}`,
    `eq=${exact}`,
  ].join("|");
};

const isSolarBuildingNotFoundError = (error) => {
  const message = String(error?.message || error || "");
  return (
    message.includes("Google Solar buildingInsights failed (404)") ||
    message.includes('"status": "NOT_FOUND"')
  );
};

const buildNearbySolarSearchPoints = (latitude, longitude) => {
  const points = [];
  for (const radiusMeters of SOLAR_BUILDING_INSIGHTS_NEARBY_RADII_METERS) {
    for (const bearingDegrees of SOLAR_BUILDING_INSIGHTS_NEARBY_BEARINGS_DEGREES) {
      const radians = (bearingDegrees * Math.PI) / 180;
      const eastMeters = Math.sin(radians) * radiusMeters;
      const northMeters = Math.cos(radians) * radiusMeters;
      const point = offsetLatLngByMeters(latitude, longitude, eastMeters, northMeters);
      if (point) points.push(point);
    }
  }
  return points;
};

const findNearbySolarBuildingInsights = async (latitude, longitude) => {
  const nearbyPoints = buildNearbySolarSearchPoints(latitude, longitude);

  for (const point of nearbyPoints) {
    try {
      return await fetchGoogleSolarBuildingInsights(point.latitude, point.longitude, {
        apiKey: GOOGLE_SOLAR_API_KEY,
        requiredQuality: "MEDIUM",
        exactQualityRequired: false,
      });
    } catch (error) {
      if (!isSolarBuildingNotFoundError(error)) throw error;
    }
  }

  return null;
};

const getCachedSolarBuildingInsights = async (latitude, longitude) => {
  const lat = parseCoordinate(latitude);
  const lng = parseCoordinate(longitude);
  if (lat == null || lng == null) {
    throw new Error("Missing latitude/longitude for Google Solar building insights.");
  }

  const key = toSolarLocationKey(lat, lng);
  if (!key) throw new Error("Failed to build Google Solar cache key.");
  if (solarInsightsCache.has(key)) return solarInsightsCache.get(key);

  const inFlight = fetchGoogleSolarBuildingInsights(lat, lng, {
    apiKey: GOOGLE_SOLAR_API_KEY,
    requiredQuality: GOOGLE_SOLAR_DEFAULT_QUALITY,
    exactQualityRequired: false,
  })
    .catch(async (error) => {
      if (!isSolarBuildingNotFoundError(error)) throw error;

      try {
        return await fetchGoogleSolarBuildingInsights(lat, lng, {
          apiKey: GOOGLE_SOLAR_API_KEY,
          requiredQuality: "MEDIUM",
          exactQualityRequired: false,
        });
      } catch (mediumError) {
        if (!isSolarBuildingNotFoundError(mediumError)) throw mediumError;
      }

      const nearbyMatch = await findNearbySolarBuildingInsights(lat, lng);
      if (nearbyMatch) return nearbyMatch;

      throw error;
    })
    .catch((error) => {
      solarInsightsCache.delete(key);
      throw error;
    });

  solarInsightsCache.set(key, inFlight);
  return inFlight;
};

const getCachedSolarDataLayers = async (
  latitude,
  longitude,
  {
    radiusMeters = 100,
    view = "FULL_LAYERS",
    requiredQuality = GOOGLE_SOLAR_DEFAULT_QUALITY,
    exactQualityRequired = false,
    pixelSizeMeters = 0.1,
  } = {}
) => {
  const lat = parseCoordinate(latitude);
  const lng = parseCoordinate(longitude);
  if (lat == null || lng == null) {
    throw new Error("Missing latitude/longitude for Google Solar data layers.");
  }

  const key = toSolarDataLayersCacheKey(lat, lng, {
    radiusMeters,
    view,
    requiredQuality,
    exactQualityRequired,
    pixelSizeMeters,
  });
  if (!key) throw new Error("Failed to build Google Solar cache key.");
  if (solarDataLayersCache.has(key)) return solarDataLayersCache.get(key);

  const inFlight = fetchGoogleSolarDataLayers(lat, lng, {
    apiKey: GOOGLE_SOLAR_API_KEY,
    radiusMeters,
    view,
    requiredQuality,
    exactQualityRequired,
    pixelSizeMeters,
  }).catch((error) => {
    solarDataLayersCache.delete(key);
    throw error;
  });

  solarDataLayersCache.set(key, inFlight);
  return inFlight;
};

const getSolarBuildingAnchor = (buildingInsights, fallbackLatitude, fallbackLongitude) => {
  const centerLat = toFiniteNumber(buildingInsights?.center?.latitude, null);
  const centerLng = toFiniteNumber(buildingInsights?.center?.longitude, null);

  return {
    latitude: centerLat ?? toFiniteNumber(fallbackLatitude, null),
    longitude: centerLng ?? toFiniteNumber(fallbackLongitude, null),
  };
};

export const fetchLidarData = async (lattitude, longitude, address, options = {}) => {
  // Kept compatible with the legacy address argument. Pipeline progress is
  // optional and is only emitted by the PLY fallback.
  return getLidar(lattitude, longitude, options);
}

export const getLidar = async (lattitude, longitude, options = {}) => {
  const onLidarProgress =
    typeof options?.onLidarProgress === "function" ? options.onLidarProgress : undefined;

  try {
    const lat = parseCoordinate(lattitude);
    const lon = parseCoordinate(longitude);
    if (lat == null || lon == null) {
      throw new Error("Missing latitude/longitude for lidar request.");
    }
    if (!GOOGLE_SOLAR_API_KEY) {
      throw new Error("Missing Google Solar API key.");
    }

    const mapCoverage = estimateMapStaticCoverageMeters(
      {
        mode: "centerZoom",
        centerLat: lat,
        centerLng: lon,
        zoom: MAP_TILE_ZOOM,
        width: MAP_TILE_SIZE,
        height: MAP_TILE_SIZE,
        retina: MAP_TILE_SCALE > 1,
        scale: MAP_TILE_SCALE,
      },
      130
    );
    const coverageWidthMeters = toFiniteNumber(mapCoverage?.widthMeters, 130);
    const coverageHeightMeters = toFiniteNumber(mapCoverage?.heightMeters, 130);
    const mapCoverageHalfSpanMeters = Math.max(coverageWidthMeters, coverageHeightMeters) * 0.5;

    const requestedRadiusMetersRaw = Math.min(
      SOLAR_FULL_LAYERS_MAX_RADIUS_METERS,
      Math.max(40, Math.ceil(mapCoverageHalfSpanMeters + 3))
    );
    const requestedPixelSizeMeters = pickSupportedSolarPixelSize(
      requestedRadiusMetersRaw,
      0.1
    );
    const requestedRadiusMeters =
      requestedRadiusMetersRaw > 100
        ? Math.min(requestedRadiusMetersRaw, Math.floor(requestedPixelSizeMeters * 1000))
        : requestedRadiusMetersRaw;

    const buildingInsights = await getCachedSolarBuildingInsights(lat, lon);
    const solarAnchor = getSolarBuildingAnchor(buildingInsights, lat, lon);
    const dataLayers = await getCachedSolarDataLayers(solarAnchor.latitude, solarAnchor.longitude, {
      radiusMeters: requestedRadiusMeters,
      view: "FULL_LAYERS",
      requiredQuality: GOOGLE_SOLAR_DEFAULT_QUALITY,
      exactQualityRequired: false,
      pixelSizeMeters: requestedPixelSizeMeters,
    });

    let lidar = null;
    let plyLidarError = null;
    try {
      lidar = await buildLidarFromGoogleSolarDataLayers(dataLayers, {
        apiKey: GOOGLE_SOLAR_API_KEY,
        latitude: solarAnchor.latitude,
        longitude: solarAnchor.longitude,
        radiusMeters: requestedRadiusMeters,
        footprintWidthMeters: coverageWidthMeters,
        footprintHeightMeters: coverageHeightMeters,
        maxPoints: null,
      });
    } catch (error) {
      // An unusable DSM gets the same treatment as no Solar DSM: try USGS/DSM
      // pipeline data before considering the legacy synthetic terrain.
      console.warn("Google Solar DSM decode failed; trying Dronify PLY fallback.", error);
    }

    if (!lidar?.points?.[0]?.points?.length) {
      try {
        lidar = await fetchDronifyPlyLidar(lat, lon, { onProgress: onLidarProgress });
      } catch (error) {
        plyLidarError = error;
        console.warn("Dronify PLY fallback failed; using synthetic LiDAR.", error);
        lidar = buildSyntheticLidarFromBuildingInsights(buildingInsights, {
          latitude: lat,
          longitude: lon,
          includeGround: true,
          maxPoints: 50000,
          sampleStepMeters: 1.25,
          footprintWidthMeters: coverageWidthMeters,
          footprintHeightMeters: coverageHeightMeters,
          groundStepMeters: 2.5,
        });
      }
    }

    const roofSegments = normalizeRoofSegmentStats(buildingInsights);
    if (!lidar?.points?.length || !lidar.points[0]?.points?.length) {
      throw new Error("Google Solar response did not contain usable lidar points.");
    }

    return {
      data: {
        ...lidar,
        roofSegments:
          (Array.isArray(lidar?.roofSegmentStats) && lidar.roofSegmentStats.length
            ? lidar.roofSegmentStats
            : roofSegments),
        mapCoverageMeters: mapCoverage || null,
        googleSolar: buildGoogleSolarSummary(buildingInsights, dataLayers),
        googleSolarRaw: {
          buildingInsights,
          dataLayers,
          plyLidarError: plyLidarError ? String(plyLidarError?.message || plyLidarError) : null,
          requestedDataLayers: {
            radiusMetersRaw: requestedRadiusMetersRaw,
            radiusMeters: requestedRadiusMeters,
            pixelSizeMeters: requestedPixelSizeMeters,
            supportedPixelSizes: SOLAR_PIXEL_SIZE_OPTIONS,
            view: "FULL_LAYERS",
          },
        },
      },
    };
  } catch (error) {
    // Solar can fail before it has returned Building Insights or Data Layers.
    // In that case the PLY pipeline is the only real-terrain fallback.
    const lat = parseCoordinate(lattitude);
    const lon = parseCoordinate(longitude);
    if (lat == null || lon == null) throw error;
    try {
      const lidar = await fetchDronifyPlyLidar(lat, lon, { onProgress: onLidarProgress });
      return {
        data: {
          ...lidar,
          roofSegments: [],
          mapCoverageMeters: null,
          googleSolar: null,
          googleSolarRaw: {
            buildingInsights: null,
            dataLayers: null,
            lidarDecodeError: String(error?.message || error),
            plyLidarError: null,
            requestedDataLayers: null,
          },
        },
      };
    } catch (plyError) {
      throw error;
    }
  }
}

export const fetchGTI = async (lattitude, longitude, tilt, azimuth) => {
  try {
    const response = await axios.get(`${LIDAR_URL}/get-gti?lat=${lattitude}&lon=${longitude}&tilt=${tilt}&azimuth=${azimuth}`)
    // const response = await axios.get(`https://re.jrc.ec.europa.eu/api/v5_2/seriescalc?lat=${lattitude}&lon=${longitude}&raddatabase=PVGIS-NSRDB&startyear=2015&endyear=2015&angle=${tilt}&aspect=${azimuth}&outputformat=json`)    
    return response;
  } catch (error) {
    throw error
  }
}

export const fetchProduction = async (params) => {
  try {
    const response = await axios.post(`${LIDAR_URL}/get-segment-productions`, params)
    return response;
  } catch (error) {
    throw error;
  }
} 

export const fetchTOF = async (params) => {
  try {
    const response = await axios.post(`${LIDAR_URL}/get-tof`, params);
    return response.data.results;    
  } catch (error) {
    throw error
  }
}
